import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2, X } from 'lucide-react';
import { API } from '../../../lib/api';
import { fmtINR } from './shared';

const STATUS = {
  pending: 'bg-amber-50 text-amber-800', confirmed: 'bg-sky-50 text-sky-800', completed: 'bg-emerald-50 text-emerald-800',
  closed: 'bg-slate-200 text-slate-900 border border-slate-400', cancelled: 'bg-rose-50 text-rose-700', no_show: 'bg-slate-100 text-slate-600',
};
const day = (d) => (d ? new Date(String(d).length === 10 ? `${d}T00:00:00` : d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const Pill = ({ s }) => <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${STATUS[s] || 'bg-slate-100 text-slate-700'}`}>{String(s || '').replace('_', ' ')}</span>;

const Row = ({ onClick, testid, title, sub, right, status }) => (
  <li>
    <button type="button" onClick={onClick} className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-left hover:bg-slate-50" data-testid={testid}>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-slate-900">{title}</span>
        <span className="block truncate text-xs text-slate-500">{sub}</span>
      </span>
      {right && <span className="font-mono text-xs text-slate-700">{right}</span>}
      <Pill s={status} />
    </button>
  </li>
);

// Side panel: a customer's appointments and job cards, newest first.
export const CustomerHistoryPanel = ({ customer, authHeader, onClose }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('appointments');
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/appointments/customer-history/${customer.id}`, { headers: authHeader() })
      .then(({ data: d }) => setData(d)).catch(() => setData({ appointments: [], job_cards: [] }));
  }, [customer.id, authHeader]);
  useEffect(() => {
    const esc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  const tabs = [['appointments', 'Appointments', data?.appointments.length], ['job_cards', 'Job cards', data?.job_cards.length]];
  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-slate-900/30" onClick={onClose} data-testid="customer-history-panel">
      <aside className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-slate-100 p-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{[customer.prefix, customer.name].filter(Boolean).join(' ')}</h2>
            <p className="text-xs text-slate-500">{customer.phone} · booking history</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="customer-history-close"><X size={18} className="text-slate-500" /></button>
        </div>
        <div className="flex gap-1 px-4 pt-3">
          {tabs.map(([k, l, n]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`customer-history-tab-${k}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${tab === k ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {l}{n !== undefined ? ` (${n})` : ''}
            </button>
          ))}
        </div>
        <ul className="flex-1 space-y-2 overflow-y-auto p-4">
          {data === null && <li><Loader2 size={16} className="animate-spin text-slate-400" /></li>}
          {data && tab === 'appointments' && (data.appointments.length ? data.appointments.map((a) => (
            <Row key={a.id} testid={`customer-history-apt-${a.id}`} onClick={() => navigate(`/admin/appointments/${a.id}`)} status={a.status}
              title={`${day(a.slot_date)} · ${a.service_type}`} sub={[a.ref_no, a.slot_window, a.linked_job_card_number && `→ ${a.linked_job_card_number}`].filter(Boolean).join(' · ')}
              right={a.packages_total ? `₹ ${fmtINR(a.packages_total)}` : ''} />
          )) : <li className="text-sm text-slate-500" data-testid="customer-history-empty">No appointments yet.</li>)}
          {data && tab === 'job_cards' && (data.job_cards.length ? data.job_cards.map((j) => (
            <Row key={j.id} testid={`customer-history-jc-${j.id}`} onClick={() => navigate(`/admin/job-cards/${j.id}`)} status={j.status}
              title={j.number} sub={`Opened ${day(j.created_at)}${j.promised_date ? ` · promised ${day(j.promised_date)}` : ''}`}
              right={`₹ ${fmtINR(j.total || 0)}`} />
          )) : <li className="text-sm text-slate-500" data-testid="customer-history-empty">No job cards yet.</li>)}
        </ul>
      </aside>
    </div>
  );
};
